// useFilter.js
import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const useFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const paramsObject = useMemo(() => {
    const params = {};
    searchParams.forEach((value, key) => {
      if (value !== "" && value !== null && value !== undefined) {
        params[key] = value;
      }
    });
    return params;
  }, [searchParams]);

  useEffect(() => {
    const cleanedParams = new URLSearchParams();

    searchParams.forEach((value, key) => {
      if (value !== "" && value !== null && value !== undefined) {
        cleanedParams.set(key, value);
      }
    });

    // Only update URL if something actually changed
    if (cleanedParams.toString() !== searchParams.toString()) {
      setSearchParams(cleanedParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Update only changed keys, preserve others
  const setFilter = useCallback(
    (newFilters) => {
      setSearchParams((currentParams) => {
        const updatedParams = new URLSearchParams(currentParams);

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === "" || value === null || value === undefined) {
            updatedParams.delete(key);
          } else {
            updatedParams.set(key, String(value));
          }
        });

        // ======= NEW LOGIC =======
        // Reset page only if a non-page filter (with non-empty value) changed
        const changedEntries = Object.entries(newFilters).filter(
          ([, value]) => value !== "" && value !== null && value !== undefined,
        );

        const hasOnlyPageChanges = changedEntries.every(
          ([key]) => key === "page" || key.endsWith("_page"),
        );

        if (!hasOnlyPageChanges) {
          updatedParams.set("page", "1");
        }
        // ==========================

        return updatedParams;
      });
    },
    [setSearchParams],
  );

  // Clear a single filter or all
  const clearFilter = useCallback(
    (key) => {
      setSearchParams((currentParams) => {
        const updatedParams = new URLSearchParams(currentParams);

        if (key) {
          updatedParams.delete(key);
        } else {
          updatedParams.forEach((_, k) => {
            if (k !== "page") updatedParams.delete(k);
          });
        }

        return updatedParams;
      });
    },
    [setSearchParams],
  );

  // Getter for table-specific search
  const getTableSearch = useCallback(
    (tableKey) => {
      return paramsObject[`${tableKey}_search`] || "";
    },
    [paramsObject],
  );

  // Setter for table-specific search
  const setTableSearch = useCallback(
    (tableKey, searchValue) => {
      setFilter({ [`${tableKey}_search`]: searchValue });
    },
    [setFilter],
  );

  // Getter for table-specific page
  const getTablePage = useCallback(
    (tableKey) => {
      const page = paramsObject[`${tableKey}_page`];
      return page ? parseInt(page) : 1;
    },
    [paramsObject],
  );

  // Setter for table-specific page
  const setTablePage = useCallback(
    (tableKey, pageNumber) => {
      setSearchParams((currentParams) => {
        const updatedParams = new URLSearchParams(currentParams);
        updatedParams.set(`${tableKey}_page`, String(pageNumber));
        return updatedParams;
      });
    },
    [setSearchParams],
  );

  // Clear all filters for a specific table
  const clearTableFilters = useCallback(
    (tableKey) => {
      setSearchParams((currentParams) => {
        const updatedParams = new URLSearchParams(currentParams);

        // Remove both search and page for this table
        updatedParams.delete(`${tableKey}_search`);
        updatedParams.delete(`${tableKey}_page`);

        return updatedParams;
      });
    },
    [setSearchParams],
  );

  // NEW: Get driver filter value with ID
  const getDriverFilter = useCallback(
    (driverOptions = []) => {
      const driverId = paramsObject.driver_id || "";

      if (!driverId) return { id: "", name: "" };

      const driver = driverOptions.find(
        (d) => d.id.toString() === driverId.toString(),
      );

      return driver
        ? { id: driver.id, name: driver.name }
        : { id: driverId, name: `Driver ${driverId}` };
    },
    [paramsObject],
  );

  // NEW: Set driver filter by ID
  const setDriverFilter = useCallback(
    (driverId) => {
      setFilter({ driver_id: driverId });
    },
    [setFilter],
  );

  // NEW: Clear driver filter
  const clearDriverFilter = useCallback(() => {
    setFilter({ driver_id: "" });
  }, [setFilter]);

  return {
    params: paramsObject,

    page: paramsObject.page || "",
    type: paramsObject.type || "",
    search: paramsObject.search || "",
    status: paramsObject.status || null,
    day: paramsObject.day || "",
    month: paramsObject.month || "",
    year: paramsObject.year || "",
    has_inspection: paramsObject.has_inspection || "",
    insurance: paramsObject.insurance || "",
    technical: paramsObject.technical || "",
    per_page: paramsObject.per_page || "",
    driver_id: paramsObject.driver_id || "",
    driver_name: paramsObject.driver_name || "",
    date: paramsObject.date || "",
    currency_id: paramsObject.currency_id || "",
    office_reference_no: paramsObject.office_reference_no || "",
    new_page: paramsObject.new_page || "",
    other_page: paramsObject.other_page || "",
    date_from: paramsObject.date_from || "",
    date_to: paramsObject.date_to || "",
    revenue_period: paramsObject.revenue_period || "daily",
    action: paramsObject.action || "",
    channel: paramsObject.channel || "",
    target_type: paramsObject.target_type || "",
    target_location: paramsObject.target_location || "",
    source_type: paramsObject.source_type || "",
    user_id: paramsObject.user_id || "",
    type_id: paramsObject.type_id || "",
    report_type: paramsObject.report_type || "",
    time_frame: paramsObject.time_frame || "",

    categories_search: paramsObject.categories_search || "",
    colors_search: paramsObject.colors_search || "",
    companies_search: paramsObject.companies_search || "",
    models_search: paramsObject.models_search || "",
    currencies_search: paramsObject.currencies_search || "",
    fuelType_search: paramsObject.fuelType_search || "",
    services_search: paramsObject.services_search || "",
    insurance_search: paramsObject.insurance_search || "",

    categories_page: paramsObject.categories_page || "1",
    colors_page: paramsObject.colors_page || "1",
    companies_page: paramsObject.companies_page || "1",
    models_page: paramsObject.models_page || "1",
    currencies_page: paramsObject.currencies_page || "1",
    fuelType_page: paramsObject.fuelType_page || "1",
    services_page: paramsObject.services_page || "1",
    insurance_page: paramsObject.insurance_page || "1",
    revenue_year: paramsObject.revenue_year || "",

    getParam: (key) => paramsObject[key] || "",

    getTableSearch,
    setTableSearch,
    getTablePage,
    setTablePage,
    clearTableFilters,

    getDriverFilter,
    setDriverFilter,
    clearDriverFilter,

    setFilter,
    clearFilter,
  };
};

export default useFilter;
